import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Spin, Result, Button, Descriptions, Tag, Timeline } from 'antd';
import { CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import axios from 'axios';
import API_URL from '../config/api';

interface RegistrationStatus {
  id: string;
  businessName: string;
  email: string;
  status: 'pending' | 'payment_required' | 'active' | 'failed';
  paymentStatus?: string;
  createdAt: string;
  activatedAt?: string;
}

const RegistrationStatus: React.FC = () => {
  const { registrationId } = useParams<{ registrationId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [registration, setRegistration] = useState<RegistrationStatus | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!registrationId) {
      setError('No registration ID provided');
      setLoading(false);
      return;
    }

    const fetchStatus = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/registration/${registrationId}/status`);
        if (response.data.success) {
          setRegistration(response.data.registration);
        } else {
          setError(response.data.error || 'Failed to fetch registration status');
        }
      } catch (err: any) {
        setError(err.response?.data?.error || 'Failed to fetch registration status');
      } finally {
        setLoading(false);
      }
    };

    fetchStatus();

    // Poll for status updates every 5 seconds if payment is pending
    const interval = setInterval(() => {
      if (registration?.status === 'payment_required' || registration?.status === 'pending') {
        fetchStatus();
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [registrationId, registration?.status]);

  const getStatusIcon = () => {
    switch (registration?.status) {
      case 'active':
        return <CheckCircleOutlined style={{ fontSize: 64, color: '#52c41a' }} />;
      case 'payment_required':
      case 'pending':
        return <LoadingOutlined style={{ fontSize: 64, color: '#1890ff' }} />;
      case 'failed':
        return <CloseCircleOutlined style={{ fontSize: 64, color: '#ff4d4f' }} />;
      default:
        return <ClockCircleOutlined style={{ fontSize: 64, color: '#faad14' }} />;
    }
  };

  const getStatusTag = () => {
    switch (registration?.status) {
      case 'active':
        return <Tag color="success">Active</Tag>;
      case 'payment_required':
        return <Tag color="processing">Payment Required</Tag>;
      case 'pending':
        return <Tag color="warning">Pending</Tag>;
      case 'failed':
        return <Tag color="error">Failed</Tag>;
      default:
        return <Tag>Unknown</Tag>;
    }
  };

  const getStatusMessage = () => {
    switch (registration?.status) {
      case 'active':
        return {
          status: 'success',
          title: 'Registration Complete!',
          subTitle: `Welcome to ShahoSuite, ${registration.businessName}! Your account is now active.`,
        };
      case 'payment_required':
        return {
          status: 'info',
          title: 'Payment Required',
          subTitle: 'Please complete your payment to activate your account.',
        };
      case 'pending':
        return {
          status: 'info',
          title: 'Processing Registration',
          subTitle: 'Your registration is being processed. This may take a few moments.',
        };
      case 'failed':
        return {
          status: 'error',
          title: 'Registration Failed',
          subTitle: 'There was an issue with your registration. Please contact support.',
        };
      default:
        return {
          status: 'info',
          title: 'Checking Status',
          subTitle: 'Please wait while we check your registration status.',
        };
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Spin size="large" tip="Loading registration status..." />
      </div>
    );
  }

  if (error || !registration) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', padding: 24 }}>
        <Result
          status="error"
          title="Error"
          subTitle={error || 'Registration not found'}
          extra={[
            <Button type="primary" key="home" onClick={() => navigate('/')}>
              Back to Home
            </Button>,
          ]}
        />
      </div>
    );
  }

  const statusMessage = getStatusMessage();

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', padding: 24, background: '#f0f2f5' }}>
      <Card style={{ maxWidth: 800, width: '100%', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <Result
          icon={getStatusIcon()}
          status={statusMessage.status as any}
          title={statusMessage.title}
          subTitle={statusMessage.subTitle}
          extra={[
            registration.status === 'active' && (
              <Button type="primary" key="login" onClick={() => navigate('/login')}>
                Go to Login
              </Button>
            ),
            registration.status === 'payment_required' && (
              <Button type="primary" key="pay" onClick={() => navigate(`/registration/${registrationId}/payment`)}>
                Complete Payment
              </Button>
            ),
            <Button key="refresh" onClick={() => window.location.reload()}>
              Refresh Status
            </Button>,
          ]}
        />

        <Descriptions title="Registration Details" bordered column={1} style={{ marginTop: 24 }}>
          <Descriptions.Item label="Business Name">{registration.businessName}</Descriptions.Item>
          <Descriptions.Item label="Email">{registration.email}</Descriptions.Item>
          <Descriptions.Item label="Status">{getStatusTag()}</Descriptions.Item>
          <Descriptions.Item label="Registration ID">{registration.id}</Descriptions.Item>
          <Descriptions.Item label="Created At">
            {new Date(registration.createdAt).toLocaleString()}
          </Descriptions.Item>
          {registration.activatedAt && (
            <Descriptions.Item label="Activated At">
              {new Date(registration.activatedAt).toLocaleString()}
            </Descriptions.Item>
          )}
        </Descriptions>

        <Timeline style={{ marginTop: 32 }} mode="left">
          <Timeline.Item color="green">
            Registration Created - {new Date(registration.createdAt).toLocaleString()}
          </Timeline.Item>
          {registration.status === 'payment_required' && (
            <Timeline.Item color="blue">
              <LoadingOutlined /> Awaiting Payment
            </Timeline.Item>
          )}
          {registration.status === 'pending' && (
            <Timeline.Item color="blue">
              <LoadingOutlined /> Processing Registration
            </Timeline.Item>
          )}
          {registration.status === 'active' && (
            <Timeline.Item color="green">
              <CheckCircleOutlined /> Account Activated - {new Date(registration.activatedAt || '').toLocaleString()}
            </Timeline.Item>
          )}
          {registration.status === 'failed' && (
            <Timeline.Item color="red">
              <CloseCircleOutlined /> Registration Failed
            </Timeline.Item>
          )}
        </Timeline>

        {(registration.status === 'payment_required' || registration.status === 'pending') && (
          <div style={{ textAlign: 'center', marginTop: 24, padding: 16, background: '#e6f7ff', borderRadius: 8 }}>
            <Spin /> <span style={{ marginLeft: 8 }}>Automatically checking for updates...</span>
          </div>
        )}
      </Card>
    </div>
  );
};

export default RegistrationStatus;
